// Auth screens layout (log in, code, two-step, legal, business setup): the logo gradient fills the
// page and the screen sits on one compact white card (owner design, 2026-10-21; D-039 colours,
// D-042 sidebar scale). Always light, whatever the theme choice. Card max 400 px, tight spacing.
// With `showcase` (log in, code and business setup; owner design 2026-10-25, D-063): a light page and one wide card,
// root's feature stage on the left (on top on a phone) and the screen on the right.
import { useLayoutEffect, type ReactNode } from "react";
import { t } from "@ady/i18n";
import { Logo } from "@ady/ui";
import { MockBanner } from "../shell/AppShell";
import { forceLight } from "../theme";

export function AuthLayout({
  title,
  lead,
  children,
  testId,
  showcase,
}: {
  title: string;
  lead?: ReactNode;
  children: ReactNode;
  testId: string;
  /** root's stage beside the form (log in, code and business setup). */
  showcase?: ReactNode;
}) {
  useLayoutEffect(() => forceLight(), []);
  if (showcase) {
    return (
      <div className="flex min-h-dvh flex-col bg-canvas">
        <MockBanner />
        <main
          id="main"
          className="ady-auth-wash relative flex flex-1 items-center justify-center overflow-hidden px-4 py-6 lg:px-8"
          data-testid={testId}
        >
          <div className="relative flex w-full max-w-280 flex-col overflow-hidden rounded-panel border border-card bg-surface shadow-3 lg:h-160 lg:flex-row">
            <div className="flex justify-center px-5 pt-5 lg:hidden">
              <Logo variant="lockup" height={26} className="max-w-full" />
            </div>
            <section
              aria-label={t("aiMascot.showcase.label")}
              className="ady-bg-brand-tint relative h-95 flex-none lg:h-auto lg:w-[57%] lg:border-e lg:border-subtle"
            >
              {showcase}
            </section>
            <div className="flex flex-1 flex-col justify-center gap-5 px-5 py-6 sm:px-10 lg:px-12">
              <Logo
                variant="lockup"
                height={34}
                className="hidden max-w-full self-start lg:block"
              />
              <div className="flex flex-col gap-1 text-center lg:text-start">
                <h1 className="text-title-md font-bold">{title}</h1>
                {lead ? <p className="text-sm text-fg-secondary">{lead}</p> : null}
              </div>
              <div className="flex w-full flex-col gap-3">{children}</div>
            </div>
          </div>
        </main>
      </div>
    );
  }
  return (
    <div className="ady-auth-bg flex min-h-dvh flex-col">
      <MockBanner />
      <main
        id="main"
        className="flex flex-1 items-center justify-center px-4 py-6"
        data-testid={testId}
      >
        <div className="flex w-full max-w-100 flex-col items-center gap-4 rounded-panel bg-surface px-5 py-6 shadow-3 sm:px-7">
          <Logo variant="lockup" height={30} className="max-w-full" />
          <div className="flex flex-col items-center gap-1 text-center">
            <h1 className="text-title-sm font-bold">{title}</h1>
            {lead ? <p className="text-sm text-fg-secondary">{lead}</p> : null}
          </div>
          <div className="flex w-full flex-col gap-3">{children}</div>
        </div>
      </main>
    </div>
  );
}
